import {
    authenticatedApi,
    ApiError,
    API_BASE_URL
} from './apiUtils';

export const skillsApi = {
  getAllSkills: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/skills`);
    } catch (error) {
      throw new ApiError('Failed to fetch skills', error.status || 500);
    }
  },

  getSkillsMatrixHierarchy: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/skills-matrices/hierarchy`);
    } catch (error) {
      throw new ApiError('Failed to fetch skills matrix hierarchy', error.status || 500);
    }
  },

  getSkillsMatrixTemplatesByClusterId: async (clusterId, reviewType) => {
    try {
      const queryParams = new URLSearchParams();
      if (reviewType) queryParams.append('reviewType', reviewType);
      
      const queryString = queryParams.toString();
      return await authenticatedApi.get(`${API_BASE_URL}/skills-matrices/templates/cluster/${clusterId}${queryString ? `?${queryString}` : ''}`);
    } catch (error) {
      throw new ApiError('Failed to fetch available skills matrix templates for cluster', error.status || 500);
    }
  },

  getSkillsMatrixTemplatesBySessionId: async (sessionId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/skills-matrices/sessions/${sessionId}/templates`);
    } catch (error) {
      throw new ApiError('Failed to fetch available skills matrix templates for session', error.status || 500);
    }
  },

  getSkillTypes: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/skills/types`);
    } catch (error) {
      throw new ApiError('Failed to fetch skill types', error.status || 500);
    }
  },

  getSkillById: async (skillId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/skills/${skillId}`);
    } catch (error) {
      throw new ApiError('Failed to fetch skill', error.status || 500);
    }
  },

  createSkill: async (skillData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/skills`, skillData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.message || 'Invalid create skill request', 400);
      }
      if (error.status === 409) {
        throw new ApiError(error.data?.message || 'Skill already exists', 409);
      }
      throw new ApiError('Failed to create skill', error.status || 500);
    }
  },

  updateSkill: async (skillId, skillData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/skills/${skillId}`, skillData);
    } catch (error) {
      if (error.status === 409) {
        throw new ApiError(error.data?.message || 'Skill already exists', 409);
      }
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid update skill request', 400);
      }
      throw new ApiError('Failed to update skill', error.status || 500);
    }
  },

  deleteSkill: async (skillId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/skills/${skillId}`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Failed to delete skill', 400);
      }
      throw new ApiError('Failed to delete skill', error.status || 500);
    }
  },

  // Skills Matrix endpoints
  getAllSkillsMatrices: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/skills-matrices`);
    } catch (error) {
      throw new ApiError('Failed to fetch skills matrices', error.status || 500);
    }
  },

  getAllSkillsMatricesBySession: async (sessionId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/skills-matrices/sessions/${sessionId}/matrices`);
    } catch (error) {
      throw new ApiError('Failed to fetch skills matrices for session', error.status || 500);
    }
  },

  getSkillsMatrixById: async (matrixId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/skills-matrices/${matrixId}`);
    } catch (error) {
      throw new ApiError('Failed to fetch skills matrix', error.status || 500);
    }
  },

  createSkillsMatrix: async (matrixData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/skills-matrices`, matrixData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid create skills matrix request', 400);
      }
      throw new ApiError(error.data?.error || 'Failed to create skills matrix', error.status || 500);
    }
  },

  updateSkillsMatrix: async (matrixId, matrixData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/skills-matrices/${matrixId}`, matrixData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid update skills matrix request', 400);
      }
      throw new ApiError(error.data?.error || 'Failed to update skills matrix', error.status || 500);
    }
  },

  deleteSkillsMatrix: async (matrixId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/skills-matrices/${matrixId}`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.message || 'Failed to delete skills matrix', 400);
      }
      throw new ApiError(error.data?.error || 'Failed to delete skills matrix', error.status || 500);
    }
  },

  // Skills Matrix Template endpoints
  getTemplatesByMatrixId: async (matrixId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/skills-matrices/${matrixId}/templates`);
    } catch (error) {
      throw new ApiError('Failed to fetch matrix templates', error.status || 500);
    }
  },

  getTemplateById: async (templateId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/skills-matrices/templates/${templateId}`);
    } catch (error) {
      throw new ApiError('Failed to fetch template', error.status || 500);
    }
  },

  getSkillTemplate: async (templateId, skillId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/skills-matrices/templates/${templateId}/skills/${skillId}`);
    } catch (error) {
      throw new ApiError('Failed to fetch skill template', error.status || 500);
    }
  },

  attachSkillToTemplate: async (templateId, data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/skills-matrices/templates/${templateId}/skills`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.message || 'Invalid skill data', 400);
      }
      throw new ApiError('Failed to attach skill to template', error.status || 500);
    }
  },

  removeSkillFromTemplate: async (templateId, skillId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/skills-matrices/templates/${templateId}/skills/${skillId}`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.message || 'Failed to remove skill from template', 400);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.message || 'Skill not found', 404);
      }
      throw new ApiError('Failed to remove skill from template', error.status || 500);
    }
  },

  updateSkillsTemplateWeights: async (templateId, data) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/skills-matrices/templates/${templateId}/weights`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid weights data', 400);
      }
      throw new ApiError('Failed to update skill weights', error.status || 500);
    }
  },

  attachSegmentsToSkillTemplate: async (templateId, skillId, data) => {
    try {
      return await authenticatedApi.post(
        `${API_BASE_URL}/skills-matrices/templates/${templateId}/skills/${skillId}/segments`,
        data
      );
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid segment data', 400);
      }
      throw new ApiError('Failed to attach segments to skill template', error.status || 500);
    }
  },

  removeSegmentFromSkillTemplate: async (templateId, skillId, segmentId) => {
    try {
      return await authenticatedApi.delete(
        `${API_BASE_URL}/skills-matrices/templates/${templateId}/skills/${skillId}/segments/${segmentId}`
      );
    } catch (error) {
      throw new ApiError('Failed to remove segment from skill template', error.status || 500);
    }
  },

  updateSkillsTemplateThreshold: async (thresholdId, data) => {
    try {
      return await authenticatedApi.put(
        `${API_BASE_URL}/skills-matrices/templates/thresholds/${thresholdId}`,
        data
      );
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid threshold data', 400);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Threshold not found', 404);
      }
      throw new ApiError('Failed to update threshold', error.status || 500);
    }
  },

  updateSkillsMatrixTemplate: async (templateId, data) => {
    try {
      return await authenticatedApi.patch(`${API_BASE_URL}/skills-matrices/templates/${templateId}`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.message || 'Invalid template data', 400);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.message || 'Template not found', 404);
      }
      throw new ApiError('Failed to update template', error.status || 500);
    }
  },

  createSkillsMatrixTemplate: async (matrixId, data) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/skills-matrices/${matrixId}/templates`, data);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid template data', 400);
      }
      throw new ApiError('Failed to create skills matrix template', error.status || 500);
    }
  },

  deleteSkillsMatrixTemplate: async (templateId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/skills-matrices/templates/${templateId}`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.message || 'Failed to delete skills matrix template', 400);
      }
      throw new ApiError('Failed to delete skills matrix template', error.status || 500);
    }
  },

  publishSkillsMatrixTemplate: async (templateId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/skills-matrices/templates/${templateId}/publish`);
    } catch (error) {
      throw new ApiError('Failed to publish skills matrix template', error.status || 500);
    }
  },

  unpublishSkillsMatrixTemplate: async (templateId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/skills-matrices/templates/${templateId}/unpublish`);
    } catch (error) {
      throw new ApiError('Failed to unpublish skills matrix template', error.status || 500);
    }
  },

  createSkillsMatrixAssignment: async (data) => {
    try {
      const { employeeId, templateId, reviewerIds, matrixId } = data;
      return await authenticatedApi.post(`${API_BASE_URL}/skills-matrices/assignments`, {
        skillsMatrixId: matrixId,
        skillsMatrixTemplateId: templateId,
        employeeId,
        reviewers: reviewerIds,
        deadlineAt: data.deadlineAt
      });
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid assignment data', 400);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Skills matrix template not found', 404);
      }
      throw new ApiError('Failed to create skills matrix assignment', error.status || 500);
    }
  },

  getEmployeeAssignments: async (employeeId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/skills-matrices/assignments/employee/${employeeId}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Employee not found', 404);
      }
      throw new ApiError('Failed to fetch employee assignments', error.status || 500);
    }
  },

  getEmployeeAssignmentById: async (assignmentId, employeeId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/skills-matrices/assignments/${assignmentId}/employee/${employeeId}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Assignment not found', 404);
      }
      throw new ApiError('Failed to fetch assignment details', error.status || 500);
    }
  },

  startEmployeeAssignment: async (reviewerId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/skills-matrices/assignments/reviewers/${reviewerId}/start`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid reviewer ID', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'You are not authorized to start assignment', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.message || 'Reviewer not found', 404);
      }
      throw new ApiError('Failed to start assignment', error.status || 500);
    }
  },

  updateSkillSegmentMappingCompetency: async (reviewerId, mappingId, competencyLevelId) => {
    try {
      return await authenticatedApi.post(
        `${API_BASE_URL}/skills-matrices/assignments/reviewers/${reviewerId}/skill-segment-mapping/${mappingId}/competency`,
        { competencyLevelId }
      );
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.message || 'Invalid competency data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Reviewer or mapping not found', 404);
      }
      throw new ApiError('Failed to update competency level', error.status || 500);
    }
  },

  updateSkillMappingCompetency: async (reviewerId, mappingId, competencyLevelId) => {
    try {
      return await authenticatedApi.post(
        `${API_BASE_URL}/skills-matrices/assignments/reviewers/${reviewerId}/skill-mapping/${mappingId}/competency`,
        { competencyLevelId }
      );
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid competency data', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'You are not authorized to update competency level', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Reviewer or mapping not found', 404);
      }
      throw new ApiError('Failed to update competency level', error.status || 500);
    }
  },

  updateSkillMappingComments: async (reviewerId, mappingId, comments) => {
    try {
      return await authenticatedApi.post(
        `${API_BASE_URL}/skills-matrices/assignments/reviewers/${reviewerId}/skill-mapping/${mappingId}/comments`,
        { comments }
      );
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid comments data', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'You are not authorized to add comments to skill mapping', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Reviewer or skill template not found', 404);
      }
      throw new ApiError('Failed to add comments to skill mapping', error.status || 500);
    }
  },

  updateSkillSegmentMappingComments: async (reviewerId, skillsTemplateMappingId, comments) => {
    try {
      return await authenticatedApi.post(
        `${API_BASE_URL}/skills-matrices/assignments/reviewers/${reviewerId}/skill-segment-mapping/${skillsTemplateMappingId}/comments`,
        { comments }
      );
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid comments data', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'You are not authorized to add comments to skill segment mapping', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Reviewer or skill segment mapping not found', 404);
      }
      throw new ApiError('Failed to add comments to skill segment mapping', error.status || 500);
    }
  },

  submitEmployeeAssignment: async (reviewerId) => {
    try {
      return await authenticatedApi.post(
        `${API_BASE_URL}/skills-matrices/assignments/reviewers/${reviewerId}/submit`
      );
    } catch (error) {
      if (error.status === 400) {
        // The submit guard answers `{ message, missingSkills }`; keep the body so
        // the caller can name the skills instead of a generic toast (ADR-195).
        throw new ApiError(
          error.data?.error || error.data?.message || 'Invalid submission request',
          400,
          error.data
        );
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'You are not authorized to submit assignment', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.message || 'Reviewer not found', 404);
      }
      throw new ApiError('Failed to submit assignment', error.status || 500);
    }
  },

  getAssignmentFeedbackActions: async (assignmentId) => {
    try {
      return await authenticatedApi.get(
        `${API_BASE_URL}/skills-matrices/assignments/${assignmentId}/feedback-actions`
      );
    } catch (error) {
      if (error.status === 404) {
        return null;
      }
      throw new ApiError('Failed to fetch feedback actions draft', error.status || 500);
    }
  },

  setAssignmentFeedbackActions: async (assignmentId, feedbackData) => {
    try {
      return await authenticatedApi.post(
        `${API_BASE_URL}/skills-matrices/assignments/${assignmentId}/feedback-actions`,
        feedbackData
      );
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.message || 'Invalid feedback actions data', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'You are not authorized to set feedback actions', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.message || 'Assignment not found', 404);
      }
      throw new ApiError('Failed to save feedback actions draft', error.status || 500);
    }
  },

  setFeedbackActions: async (reviewerId, feedbackData) => {
    try {
      return await authenticatedApi.post(
        `${API_BASE_URL}/skills-matrices/assignments/reviewers/${reviewerId}/feedback-actions`,
        feedbackData
      );
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid feedback actions data', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'You are not authorized to set feedback actions', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Reviewer or skills template not found', 404);
      }
      throw new ApiError('Failed to set feedback actions', error.status || 500);
    }
  },

  updateCompetencyDescription: async (competencyId, description) => {
    try {
      return await authenticatedApi.patch(
        `${API_BASE_URL}/skills-matrices/templates/competencies/${competencyId}/description`,
        { description }
      );
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.message || 'Invalid description', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Competency not found', 404);
      }
      throw new ApiError('Failed to update competency description', error.status || 500);
    }
  },

  updateSegmentCompetencyDescription: async (competencyId, description) => {
    try {
      return await authenticatedApi.patch(
        `${API_BASE_URL}/skills-matrices/templates/segments/competencies/${competencyId}/description`,
        { description }
      );
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.message || 'Invalid description', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Competency not found', 404);
      }
      throw new ApiError('Failed to update competency description', error.status || 500);
    }
  },

  getEmployeeTemplateMapping: async (sessionId = null) => {
    try {
      const url = sessionId
        ? `${API_BASE_URL}/skills-matrices/employee-mapping?sessionId=${sessionId}`
        : `${API_BASE_URL}/skills-matrices/employee-mapping`;
      return await authenticatedApi.get(url);
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError(error.data?.message || 'You are not authorized to access employee-template mapping', 403);
      }
      throw new ApiError('Failed to fetch employee-template mapping', error.status || 500);
    }
  },

  // Follow-up Questions endpoints
  createFollowUpQuestion: async (skillId, questionData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/skills/${skillId}/followup-questions`, questionData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.message || 'Invalid follow-up question data', 400);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.message || 'Skill not found', 404);
      }
      throw new ApiError('Failed to create follow-up question', error.status || 500);
    }
  },

  updateFollowUpQuestion: async (questionId, questionData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/skills/followup-questions/${questionId}`, questionData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.message || 'Invalid follow-up question data', 400);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.message || 'Follow-up question not found', 404);
      }
      throw new ApiError('Failed to update follow-up question', error.status || 500);
    }
  },

  updateFollowUpQuestionOrder: async (questionId, order) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/skills/followup-questions/${questionId}/order`, { order });
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.message || 'Invalid order value', 400);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.message || 'Follow-up question not found', 404);
      }
      throw new ApiError('Failed to update follow-up question order', error.status || 500);
    }
  },

  deleteFollowUpQuestion: async (questionId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/skills/followup-questions/${questionId}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError(error.data?.message || 'Follow-up question not found', 404);
      }
      throw new ApiError('Failed to delete follow-up question', error.status || 500);
    }
  },

  // Skills Matrix Sessions endpoints
  getAllSkillsMatrixSessions: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/skills-matrices/sessions`);
    } catch (error) {
      throw new ApiError('Failed to fetch skills matrix sessions', error.status || 500);
    }
  },

  getSkillsMatrixSessionById: async (sessionId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/skills-matrices/sessions/${sessionId}`);
    } catch (error) {
      throw new ApiError('Failed to fetch skills matrix session', error.status || 500);
    }
  },

  getSkillsMatrixSessionsByCluster: async (clusterId, reviewType) => {
    try {
      const queryParams = new URLSearchParams();
      if (reviewType) queryParams.append('reviewType', reviewType);
      
      const queryString = queryParams.toString();
      
      // If clusterId is null, fetch from all clusters
      const endpoint = clusterId 
        ? `${API_BASE_URL}/skills-matrices/sessions/cluster/${clusterId}`
        : `${API_BASE_URL}/skills-matrices/sessions/cluster/all`;
      
      return await authenticatedApi.get(`${endpoint}${queryString ? `?${queryString}` : ''}`);
    } catch (error) {
      const errorMessage = clusterId 
        ? 'Failed to fetch skills matrix sessions for cluster'
        : 'Failed to fetch skills matrix sessions from all clusters';
      throw new ApiError(errorMessage, error.status || 500);
    }
  },

  createSkillsMatrixSession: async (sessionData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/skills-matrices/sessions`, sessionData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid create skills matrix session request', 400);
      }
      throw new ApiError(error.data?.error || 'Failed to create skills matrix session', error.status || 500);
    }
  },

  updateSkillsMatrixSession: async (sessionId, sessionData) => {
    try {
      return await authenticatedApi.put(`${API_BASE_URL}/skills-matrices/sessions/${sessionId}`, sessionData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid update skills matrix session request', 400);
      }
      throw new ApiError(error.data?.error || 'Failed to update skills matrix session', error.status || 500);
    }
  },

  deleteSkillsMatrixSession: async (sessionId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/skills-matrices/sessions/${sessionId}`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.message || 'Failed to delete skills matrix session', 400);
      }
      throw new ApiError(error.data?.error || 'Failed to delete skills matrix session', error.status || 500);
    }
  },

  publishSkillsMatrixSession: async (sessionId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/skills-matrices/sessions/${sessionId}/publish`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid publish request', 400);
      }
      throw new ApiError('Failed to publish skills matrix session', error.status || 500);
    }
  },

  unpublishSkillsMatrixSession: async (sessionId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/skills-matrices/sessions/${sessionId}/unpublish`);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid unpublish request', 400);
      }
      throw new ApiError('Failed to unpublish skills matrix session', error.status || 500);
    }
  },

  addVersionsToSession: async (sessionId, matrixId, selectedVersions) => {
    try {
      const requestData = {
        selectedVersions: selectedVersions.map(version => ({
          id: version,
          weight: 0
        }))
      };
      return await authenticatedApi.post(`${API_BASE_URL}/skills-matrices/sessions/${sessionId}/matrices/${matrixId}/versions`, requestData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid add versions to session request', 400);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Session or versions not found', 404);
      }
      throw new ApiError(error.data?.error || 'Failed to add versions to session', error.status || 500);
    }
  },

  getSessionMatrixVersions: async (sessionId, matrixId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/skills-matrices/sessions/${sessionId}/matrices/${matrixId}/versions`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Session or matrix not found', 404);
      }
      throw new ApiError(error.data?.error || 'Failed to fetch session matrix versions', error.status || 500);
    }
  },

  // Export matrix version to another matrix template
  exportMatrixVersion: async (versionId, targetMatrixId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/skills-matrices/templates/${versionId}/export`, {
        targetMatrixId
      });
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid export request', 400);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Version or target matrix not found', 404);
      }
      throw new ApiError(error.data?.error || 'Failed to export matrix version', error.status || 500);
    }
  },

  // ===========================================================================
  // ADR-003: in-progress feedback-session modifications (Decisions 2, 3, 4, 5, 7).
  // Phase-1: admin-only on the server.
  // ===========================================================================

  // Decision 5 — reopen submitted Start/Stop/Continue on an assignment.
  reopenFeedbackActions: async (assignmentId, reason) => {
    try {
      return await authenticatedApi.post(
        `${API_BASE_URL}/skills-matrices/assignments/${assignmentId}/reopen-actions`,
        reason ? { reason } : {}
      );
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'Admin role required', 403);
      }
      if (error.status === 409) {
        throw new ApiError(error.data?.error || 'Feedback actions cannot be reopened', 409);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Assignment not found', 404);
      }
      throw new ApiError(error.data?.error || 'Failed to reopen feedback actions', error.status || 500);
    }
  },

  // Decision 3 — preview the impact of a skills-matrix change (read-only).
  getMatrixChangePreview: async (assignmentId) => {
    try {
      return await authenticatedApi.get(
        `${API_BASE_URL}/skills-matrices/assignments/${assignmentId}/matrix-change-preview`
      );
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'Admin role required', 403);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Assignment not found', 404);
      }
      throw new ApiError(error.data?.error || 'Failed to load matrix change preview', error.status || 500);
    }
  },

  // Decision 3 — change the skills matrix on a started assignment.
  changeMatrix: async (assignmentId, data) => {
    try {
      return await authenticatedApi.put(
        `${API_BASE_URL}/skills-matrices/assignments/${assignmentId}/matrix`,
        data
      );
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid matrix change request', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'Admin role required', 403);
      }
      if (error.status === 409) {
        // includes submittedFoDBlockers / currentDiscardedFoDCount / currentResponseCount
        throw new ApiError(error.data?.error || 'Matrix change conflict', 409, error.data);
      }
      throw new ApiError(error.data?.error || 'Failed to change matrix', error.status || 500);
    }
  },

  // Decision 2 — remove a reviewer from an assignment.
  removeReviewer: async (assignmentId, reviewerId, reason) => {
    try {
      return await authenticatedApi.delete(
        `${API_BASE_URL}/skills-matrices/assignments/${assignmentId}/reviewers/${reviewerId}`,
        { reason }
      );
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid remove-reviewer request', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'Admin role required', 403);
      }
      if (error.status === 409) {
        throw new ApiError(error.data?.error || 'Reviewer cannot be removed', 409);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Reviewer not found', 404);
      }
      throw new ApiError(error.data?.error || 'Failed to remove reviewer', error.status || 500);
    }
  },

  // Decision 2 — replace a reviewer (transfer or wipe previous answers).
  replaceReviewer: async (assignmentId, reviewerId, data) => {
    try {
      return await authenticatedApi.put(
        `${API_BASE_URL}/skills-matrices/assignments/${assignmentId}/reviewers/${reviewerId}/replace`,
        data
      );
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid replace-reviewer request', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'Admin role required', 403);
      }
      if (error.status === 409) {
        throw new ApiError(error.data?.error || 'Reviewer cannot be replaced', 409);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Reviewer not found', 404);
      }
      throw new ApiError(error.data?.error || 'Failed to replace reviewer', error.status || 500);
    }
  },

  // Decision 2 follow-up — switch the order of two not-yet-started Sequential
  // reviewers. `data` carries targetReviewerId and an optional reason.
  switchReviewerOrder: async (assignmentId, reviewerId, data) => {
    try {
      return await authenticatedApi.put(
        `${API_BASE_URL}/skills-matrices/assignments/${assignmentId}/reviewers/${reviewerId}/order`,
        data
      );
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid switch-order request', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'Admin role required', 403);
      }
      if (error.status === 409) {
        throw new ApiError(error.data?.error || 'Reviewer order cannot be switched', 409);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Reviewer not found', 404);
      }
      throw new ApiError(error.data?.error || 'Failed to switch reviewer order', error.status || 500);
    }
  },


  // Decision 8 — reopen a reviewer's competency review (destructive: reason required).
  reopenReview: async (reviewerId, reason) => {
    try {
      return await authenticatedApi.post(
        `${API_BASE_URL}/skills-matrices/reviewers/${reviewerId}/reopen-review`,
        { reason }
      );
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'Admin role required', 403);
      }
      if (error.status === 409) {
        throw new ApiError(error.data?.error || 'Review cannot be reopened', 409);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Reviewer not found', 404);
      }
      throw new ApiError(error.data?.error || 'Failed to reopen review', error.status || 500);
    }
  },

  // Decision 9 — reset a reviewer's competency review to "Not Started"
  // (destructive: reason required; wipes the reviewer's responses).
  resetReview: async (reviewerId, reason) => {
    try {
      return await authenticatedApi.post(
        `${API_BASE_URL}/skills-matrices/reviewers/${reviewerId}/reset-review`,
        { reason }
      );
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'Admin role required', 403);
      }
      if (error.status === 409) {
        throw new ApiError(error.data?.error || 'Review cannot be reset', 409);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Reviewer not found', 404);
      }
      throw new ApiError(error.data?.error || 'Failed to reset review', error.status || 500);
    }
  },

  // Decision 10 — reopen an interim Sequential reviewer's per-step
  // Start/Stop/Continue (non-destructive: reason optional).
  reopenReviewerActions: async (reviewerId, reason) => {
    try {
      return await authenticatedApi.post(
        `${API_BASE_URL}/skills-matrices/reviewers/${reviewerId}/reopen-actions`,
        reason ? { reason } : {}
      );
    } catch (error) {
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'Admin role required', 403);
      }
      if (error.status === 409) {
        throw new ApiError(error.data?.error || 'Start/Stop/Continue cannot be reopened', 409);
      }
      if (error.status === 404) {
        throw new ApiError(error.data?.error || 'Reviewer not found', 404);
      }
      throw new ApiError(error.data?.error || 'Failed to reopen Start/Stop/Continue', error.status || 500);
    }
  },

  // Decision 7 — add a reviewer to an assignee. `data` carries employeeId and
  // skillsMatrixTemplateId (and an optional order for Sequential reviews).
  addReviewer: async (assignmentId, data) => {
    try {
      return await authenticatedApi.post(
        `${API_BASE_URL}/skills-matrices/assignments/${assignmentId}/reviewers`,
        data
      );
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid add-reviewer request', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'Admin role required', 403);
      }
      if (error.status === 409) {
        throw new ApiError(error.data?.error || 'Reviewer cannot be added', 409);
      }
      throw new ApiError(error.data?.error || 'Failed to add reviewer', error.status || 500);
    }
  },

  // Decision 7 (bulk) — add several reviewers at once (report "Assign Team
  // Members"). `data` is { reviewers: [{ employeeId, skillsMatrixTemplateId, order? }], reason? }.
  bulkAddReviewers: async (assignmentId, data) => {
    try {
      return await authenticatedApi.post(
        `${API_BASE_URL}/skills-matrices/assignments/${assignmentId}/reviewers/bulk`,
        data
      );
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid bulk add-reviewer request', 400);
      }
      if (error.status === 403) {
        throw new ApiError(error.data?.error || 'Admin role required', 403);
      }
      if (error.status === 409) {
        throw new ApiError(error.data?.error || 'Reviewers cannot be added', 409);
      }
      throw new ApiError(error.data?.error || 'Failed to add reviewers', error.status || 500);
    }
  },

};